import React from 'react';

interface LogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const Logo: React.FC<LogoProps> = ({ className = '', size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16'
  };

  return (
    <div className={`${className} flex items-center`}>
      <div className="relative">
        <svg
          className={`${sizeClasses[size]} mr-3`}
          viewBox="0 0 120 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Gradient definitions */}
          <defs>
            <linearGradient id="mountainGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#166534" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#064e3b" stopOpacity="0.9" />
            </linearGradient>
            <linearGradient id="koruGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#0891b2" />
              <stop offset="100%" stopColor="#155e75" />
            </linearGradient>
            <radialGradient id="backgroundGradient" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#0891b2" stopOpacity="0.1" />
              <stop offset="100%" stopColor="#155e75" stopOpacity="0.05" />
            </radialGradient>
          </defs>
          
          {/* Background circle with gradient */}
          <circle 
            cx="60" 
            cy="60" 
            r="55" 
            fill="url(#backgroundGradient)"
            stroke="#0891b2"
            strokeWidth="0.5"
            opacity="0.3"
          />
          
          {/* Mountain ranges representing Te Tairawhiti landscape */}
          <path
            d="M10 85 L25 55 L35 65 L45 45 L60 60 L75 35 L90 50 L105 40 L115 85 Z"
            fill="url(#mountainGradient)"
          />
          
          {/* Central large Koru representing growth and new beginnings */}
          <path
            d="M60 20 Q40 25 45 50 Q52 70 70 60 Q85 50 75 45 Q68 42 70 47 Q72 50 70 51"
            stroke="url(#koruGradient)"
            strokeWidth="4"
            fill="none"
            strokeLinecap="round"
          />
          
          {/* Smaller supporting koru */}
          <path
            d="M30 30 Q25 32 27 37 Q30 40 32 38 Q33 37 32 37.5"
            stroke="#0891b2"
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
            opacity="0.7"
          />
          
          {/* Whakapapa connection network - enhanced */}
          <g opacity="0.85">
            {/* Ancestor generation (tipuna) */}
            <circle cx="30" cy="70" r="4.5" fill="#0891b2" />
            <circle cx="60" cy="75" r="4.5" fill="#0891b2" />
            <circle cx="90" cy="68" r="4.5" fill="#0891b2" />
            
            {/* Current generation */}
            <circle cx="20" cy="95" r="3.5" fill="#155e75" />
            <circle cx="40" cy="100" r="3.5" fill="#155e75" />
            <circle cx="80" cy="98" r="3.5" fill="#155e75" />
            <circle cx="100" cy="90" r="3.5" fill="#155e75" />
            
            {/* Connection lines with gradient */}
            <line x1="30" y1="70" x2="60" y2="75" stroke="#0891b2" strokeWidth="2.5" opacity="0.8" />
            <line x1="60" y1="75" x2="90" y2="68" stroke="#0891b2" strokeWidth="2.5" opacity="0.8" />
            
            {/* Generational flow */}
            <line x1="30" y1="70" x2="20" y2="95" stroke="#155e75" strokeWidth="2" opacity="0.7" />
            <line x1="30" y1="70" x2="40" y2="100" stroke="#155e75" strokeWidth="2" opacity="0.7" />
            <line x1="90" y1="68" x2="80" y2="98" stroke="#155e75" strokeWidth="2" opacity="0.7" />
            <line x1="90" y1="68" x2="100" y2="90" stroke="#155e75" strokeWidth="2" opacity="0.7" />
          </g>
          
          {/* Traditional Māori pattern elements (moko-inspired) */}
          <g opacity="0.6">
            <path
              d="M15 25 Q22 18 28 25 Q22 32 15 25"
              fill="#0891b2"
            />
            <path
              d="M92 20 Q99 13 105 20 Q99 27 92 20"
              fill="#0891b2"
            />
            <path
              d="M105 105 Q98 98 92 105 Q98 112 105 105"
              fill="#155e75"
            />
          </g>
          
          {/* Highlight elements */}
          <circle cx="60" cy="60" r="2" fill="#fbbf24" opacity="0.8" />
        </svg>
      </div>
      
      <div className="flex flex-col">
        <span className="font-bold text-xl text-ocean-teal tracking-wide leading-tight">He Whakaputanga</span>
        <span className="text-xs text-gray-600 font-medium italic">nga Hapū o te Tairawhiti</span>
      </div>
    </div>
  );
};

export default Logo;
